'use client';

import { useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import ErrorBox from '@/components/ErrorBox';
import LabelBadge from '@/components/LabelBadge';
import SectionTitle from '@/components/SectionTitle';
import FormFieldLabel from '@/components/ui/form/FormFieldLabel';
import FormInput from '@/components/ui/form/FormInput';
import FormInputWithLabel from '@/components/ui/form/FormInputWithLabel';
import FormSectionTitle from '@/components/ui/form/FormSectionTitle';

interface DashboardPersonalPhoneNumberAuthProps {
    editPhoneNumber: string | null;
}
function DashboardPersonalPhoneNumberAuth({ editPhoneNumber }: DashboardPersonalPhoneNumberAuthProps) {
    const { handleError, errorMessage, clearError } = useHandleError();
    const showToastAsync = useToastStore((s) => s.showToastAsync);
    const closeModal = useModalStore((state) => state.closeModal);
    const setUserRegistrationStatus = useAuthStore((s) => s.setUserRegistrationStatus);

    const handleAuthCodeInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value.length === 6) {
            try {
                clearError();
                await apiFetch(`/users/me/phone_number`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ code: value, phone_number: editPhoneNumber }),
                });
                closeModal();
                setUserRegistrationStatus({ phone_number: 1 });
                await showToastAsync('電話番号の設定が完了しました', 'success');
            } catch (err: unknown) {
                handleError(err);
            }
        }
    };

    return (
        <div className="flex flex-col w-full">
            <div className="section-spacer-inner-y"></div>
            <div className="flex justify-start items-center w-full py-4">
                <FormSectionTitle title="認証コード入力" />
            </div>
            <div className="flex flex-col justify-start items-center w-full py-4">
                <div className="flex items-start justify-start w-full">
                    <ErrorBox message={errorMessage?.message} />
                </div>
                <div className="flex flex-col items-center w-full">
                    <div className="flex justify-start items-center w-full">
                        <FormFieldLabel title="新しい電話番号" />
                    </div>
                    <div className="my-2"></div>
                    <div className="flex w-full px-4 text-base">{editPhoneNumber}</div>
                </div>
                <div className="py-6 md:py-4"></div>
                <div className="flex flex-col justify-center items-center bg-color-white-base w-full h-full">
                    <div className="flex justify-start items-center w-full mb-4">
                        <FormFieldLabel title="認証コード" />
                        <LabelBadge type="required" />
                    </div>
                    <div className="flex flex-col items-start justify-start w-[90%] xl:w-[70%]">
                        <div id="code" className="flex flex-col items-center w-full">
                            <FormInput
                                id="code"
                                name="code"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                placeholder="＊＊＊＊＊＊"
                                autoComplete="off"
                                autoFocus={true}
                                onChange={handleAuthCodeInputChange}
                                className="rounded-3xl text-center py-4"
                            />
                        </div>
                    </div>
                    <div className="section-spacer-inner-4-y"></div>
                </div>
            </div>
        </div>
    );
}
interface DashboardPersonalPhoneNumberEditProps {
    goToAuth: () => void;
    currentPhoneNumber: string | null;
    setEditPhoneNumber: (editPhoneNumber: string) => void;
}
function DashboardPersonalPhoneNumberEdit({
    goToAuth,
    currentPhoneNumber,
    setEditPhoneNumber,
}: DashboardPersonalPhoneNumberEditProps) {
    const { handleError, errorMessage, clearError } = useHandleError();
    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState({ phone_number: '' });

    const normalizeAndStripSpaces = (value: string) => {
        return value.normalize('NFKC').replace(/[\s\u3000]/g, '');
    };

    const handleAuthIdentifierBlur: React.FocusEventHandler<HTMLDivElement> = (e) => {
        const target = e.target as Element;
        if (!(target instanceof HTMLInputElement)) return;
        let value = normalizeAndStripSpaces(target.value);
        if (value) {
            value = value.replace(/[-－ー]/g, '');
        }
        handlePhoneNumberInputChange(value);
    };
    const handlePhoneNumberInputChange = (value: string) => {
        setFormData({
            ...formData,
            phone_number: value,
        });
    };

    const handleSubmit = async () => {
        try {
            clearError();
            setIsProcessing(true);
            const response = await apiFetch(`/users/me/phone_number/update/activation/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            goToAuth();
            setEditPhoneNumber(response.phone_number);
        } catch (err: unknown) {
            handleError(err);
            setIsProcessing(false);
        }
    };

    return (
        <>
            <div className="flex flex-col w-full">
                <div className="section-spacer-inner-y"></div>
                <div className="flex justify-start items-center w-full py-4">
                    <FormSectionTitle title="電話番号" />
                </div>
                <div className="flex flex-col items-start justify-start w-full py-4">
                    <div className="flex items-start justify-start w-full">
                        <ErrorBox message={errorMessage?.message} />
                    </div>
                    <div className="flex flex-col items-center w-full font-notojp">
                        <div className="flex justify-start items-center w-full">
                            <FormFieldLabel title="現在の電話番号" />
                        </div>
                        <div className="my-2"></div>
                        <div className="flex w-full px-4 text-sm md:text-base break-words break-all">
                            {currentPhoneNumber || '電話番号は登録されていません'}
                        </div>
                    </div>
                    <div className="py-6 md:py-4"></div>
                    <div id="phone_number" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full">
                            <FormFieldLabel title="新しい電話番号" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="phone_number" />
                        </div>
                        <div className="flex w-full" onBlur={handleAuthIdentifierBlur}>
                            <FormInputWithLabel
                                id="phone_number"
                                name="phone_number"
                                label="11桁以内の半角数字"
                                type="text"
                                inputMode="tel"
                                maxLength={13}
                                autoComplete="off"
                                value={formData.phone_number}
                                onChange={(e) => {
                                    handlePhoneNumberInputChange(e.target.value);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col items-start justify-start w-full py-2">
                <div className="flex flex-col-reverse md:flex-row justify-center w-full pt-12">
                    <div className="flex-grow"></div>
                    <div className="flex flex-col justify-end items-center">
                        <div className="flex justify-center items-center">
                            <ButtonGAdaptive
                                type="submit"
                                label="認証コードを送信する"
                                onClick={handleSubmit}
                                description="send verification code"
                                icon={Send}
                                isProcessing={isProcessing}
                            />
                        </div>
                    </div>
                    <div className="flex-grow"></div>
                </div>
            </div>
        </>
    );
}

export default function DashboardPersonalPhoneNumber() {
    const [viewState, setViewState] = useState<'edit' | 'auth'>('edit');
    const [editPhoneNumber, setEditPhoneNumber] = useState<string | null>(null);
    const [currentPhoneNumber, setCurrentPhoneNumber] = useState<string | null>(null);
    const { handleError } = useHandleError();

    useEffect(() => {
        (async () => {
            try {
                const data = await apiFetch(`/users/me/phone_number`);
                setCurrentPhoneNumber(data.phone_number || null);
            } catch (err: unknown) {
                handleError(err);
            }
        })();
    }, []);

    return (
        <div className="flex flex-col w-full md:w-[98%] section-4-b bg-color-white-base">
            <div className="flex w-full items-center">
                <div className="flex flex-grow justify-start items-center w-full">
                    <SectionTitle title={`Phone Number`} />
                </div>
            </div>

            {viewState === 'edit' && (
                <DashboardPersonalPhoneNumberEdit
                    goToAuth={() => setViewState('auth')}
                    currentPhoneNumber={currentPhoneNumber}
                    setEditPhoneNumber={setEditPhoneNumber}
                />
            )}
            {viewState === 'auth' && <DashboardPersonalPhoneNumberAuth editPhoneNumber={editPhoneNumber} />}
        </div>
    );
}
